/**
 * staticBatch — funde o CENÁRIO ESTÁTICO (prédios/postes/bancos/decor) em poucos
 * meshes, um por MATERIAL, cortando os draw calls do mundo de ~3.4k p/ ~centenas.
 *
 * Por quê: cada janelinha/duto/friso dos prédios é um Mesh próprio (2.9k só nos
 * prédios) e cada mesh = 1 draw call por frame — o gargalo dominante medido (o
 * custo é de CPU/driver, não de triângulos). Como NADA disso se move nem troca de
 * material em runtime, dá p/ "assar" a transformação de cada peça na geometria e
 * mesclar todas as peças do mesmo material em UM mesh — resultado visual IDÊNTICO
 * (mesmos materiais, mesmas posições), só que em ~1 chamada por material.
 *
 * Regras de segurança (sem regressão visual):
 *   • `renderOrder` ≠ 0 fica de fora (depende da ordem por objeto);
 *   • buckets separados por castShadow/receiveShadow (flags preservadas);
 *   • meshes multi-material são divididos pelos `groups` da geometria (cada
 *     pedaço vai p/ o bucket do seu material).
 *
 * DEDUPLICAÇÃO de materiais (dedupeMaterials): os builders dos prédios criam um
 * material NOVO a cada peça, então o mesmo verde aparecia como dezenas de objetos
 * distintos (medido: 922 materiais para 234 configurações reais). Como o bucket é
 * a IDENTIDADE do material, cada duplicata virava um draw call a mais. Aqui a
 * gente colapsa materiais com parâmetros IDÊNTICOS num só objeto ANTES de
 * bucketizar — visual idêntico por construção (mesmos valores), draw calls ~4x
 * menores nos prédios.
 *
TRANSPARENTES: por que agora eles TAMBÉM são fundidos
 * -----------------------------------------------------
 * A regra original mantinha todo material transparente fora do batch, porque o
 * resultado do blend depende da ordem de desenho e o three ordena transparentes
 * por profundidade, POR OBJETO. Fundir N objetos num só congela essa ordem.
 *
 * Isso vale quando duas superfícies transparentes se sobrepõem na tela. Medido
 * neste cenário, elas NUNCA se sobrepõem: raycast nos 84 pares de painéis do mesmo
 * prédio deu 84/84 com corpo OPACO no caminho (nenhuma linha de visada livre), e
 * 216 vistas (72 azimutes × 3 ângulos polares, com a câmera real) não acharam
 * nenhuma sobreposição entre prédios diferentes — a distância mínima entre prédios
 * é 25,91u. São 26 meshes transparentes virando 5 chamadas.
 *
 * Mas isso NÃO vale para todo transparente do cenário: existe pelo menos um par
 * (uma esfera e um cilindro concêntricos, material #fff1b8) que se interpenetra de
 * verdade. Por isso a decisão não é uma lista fixa — `transparentesSeguros()`
 * DECIDE POR MATERIAL, na montagem: se duas superfícies do mesmo material têm
 * caixas envolventes que se cruzam, aquele material fica fora do batch e continua
 * ordenado por profundidade, como antes. O critério é conservador (caixa
 * envolvente, não geometria exata), então erra sempre para o lado seguro, e passa
 * a valer sozinho para qualquer transparência que for acrescentada depois.
 *
 * Uso (main.js): `stage.add(batchStatic(createBuildings()))` — SÓ para grupos
 * 100% estáticos (nunca em trees/clouds/fleet, que animam).
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

/**
 * Propriedades que definem a APARÊNCIA de um material. Duas instâncias com todas
 * elas iguais produzem exatamente o mesmo pixel → podem ser o MESMO objeto.
 * Mantida conservadora de propósito: qualquer propriedade fora desta lista que
 * esteja diferente do padrão do three faz o material NÃO ser deduplicado (a
 * assinatura inclui um marcador do que não sabemos comparar).
 */
const MAT_KEYS = [
  'type', 'transparent', 'opacity', 'alphaTest', 'side', 'flatShading', 'vertexColors',
  'depthTest', 'depthWrite', 'blending', 'toneMapped', 'fog', 'wireframe', 'dithering',
  'roughness', 'metalness', 'emissiveIntensity', 'reflectivity', 'shininess',
  'clearcoat', 'clearcoatRoughness', 'transmission', 'thickness', 'ior', 'sheen',
  'iridescence', 'envMapIntensity', 'aoMapIntensity', 'lightMapIntensity',
  'displacementScale', 'displacementBias', 'bumpScale', 'premultipliedAlpha'
];
const MAT_COLORS = ['color', 'emissive', 'specular', 'attenuationColor', 'sheenColor'];
/** Slots de textura de um material — lista canônica, reusada pela passada de
 *  anisotropia do nível Ultra (main.js) para não duplicar a enumeração. */
export const MAT_MAPS = [
  'map', 'emissiveMap', 'roughnessMap', 'metalnessMap', 'normalMap', 'bumpMap',
  'alphaMap', 'aoMap', 'lightMap', 'envMap', 'displacementMap', 'specularMap', 'gradientMap'
];

/** Assinatura de aparência de um material (string). Materiais com a mesma
 *  assinatura renderizam idêntico e podem ser compartilhados. */
/**
 * Arredonda roughness/metalness para a grade de 0,1 ANTES de comparar — e grava o
 * valor arredondado no material. Materiais que só diferem por 0,85 vs 0,9 passam a
 * colapsar num bucket só: −8 draw calls no cenário. O deslocamento máximo real em
 * toda a paleta é 0,05 em roughness e 0,02 em metalness, e nenhum material cruza
 * de metálico para não-metálico.
 *
 * Gravar (e não só comparar) é obrigatório: se a assinatura arredondasse mas o
 * material canônico guardasse o valor original, o brilho renderizado do par 0,85/0,9
 * dependeria de qual dos dois o `traverse` encontrasse primeiro.
 */
const SNAP_KEYS = ['roughness', 'metalness'];
function snapMaterial(m) {
  for (const k of SNAP_KEYS) {
    if (typeof m[k] === 'number') m[k] = Math.round(m[k] * 10) / 10;
  }
}

function materialSignature(m) {
  snapMaterial(m);
  const parts = [];
  for (const k of MAT_KEYS) parts.push(m[k] === undefined ? '' : String(m[k]));
  for (const k of MAT_COLORS) parts.push(m[k] && m[k].isColor ? m[k].getHexString() : '');
  // texturas comparadas por IDENTIDADE (a mesma textura compartilhada → mesma chave)
  for (const k of MAT_MAPS) parts.push(m[k] ? m[k].uuid : '');
  if (m.normalScale) parts.push(m.normalScale.x + ',' + m.normalScale.y);
  // `onBeforeCompile` customizado ou defines mudam o shader → nunca deduplica
  if (m.onBeforeCompile && m.onBeforeCompile.length) parts.push('obc:' + m.uuid);
  if (m.defines) parts.push('def:' + JSON.stringify(m.defines));
  if (m.userData && Object.keys(m.userData).length) parts.push('ud:' + JSON.stringify(m.userData));
  return parts.join('|');
}

/**
 * Substitui, IN-PLACE, materiais duplicados do grupo por uma única instância
 * compartilhada por assinatura. Devolve `{ before, after }` (p/ diagnóstico).
 *
 * Seguro por construção: só troca A por B quando TODAS as propriedades de
 * aparência são iguais — o pixel resultante é o mesmo. Materiais não mesclados
 * (transparentes) também se beneficiam: passam a compartilhar programa/uniformes,
 * o que reduz troca de estado no driver mesmo sem reduzir draw calls.
 */
export function dedupeMaterials(group) {
  const canon = new Map(); // assinatura → material canônico
  let before = new Set();
  group.traverse((o) => {
    if (!o.isMesh || !o.material) return;
    const arr = Array.isArray(o.material) ? o.material : null;
    const list = arr || [o.material];
    for (let i = 0; i < list.length; i++) {
      const m = list[i];
      if (!m) continue;
      before.add(m.uuid);
      const sig = materialSignature(m);
      const c = canon.get(sig);
      if (!c) { canon.set(sig, m); continue; }
      if (c === m) continue;
      if (arr) arr[i] = c;
      else o.material = c;
    }
  });
  return { before: before.size, after: canon.size };
}

/**
 * Quais materiais TRANSPARENTES podem ser fundidos sem mudar a imagem.
 *
 * Fundir congela a ordem de desenho. Com o mesmo material, isso só muda o pixel se
 * duas superfícies se sobrepuserem na tela — e o teste conservador para "podem se
 * sobrepor" é a interseção das caixas envolventes em MUNDO. Materiais cujas
 * superfícies nunca se cruzam entram no batch; os demais seguem pelo caminho
 * antigo (um draw call cada, ordenados por profundidade pelo three).
 *
 * @returns {Set} materiais transparentes liberados para fusão
 */
function transparentesSeguros(group) {
  const porMat = new Map(); // material → caixas envolventes de mundo
  group.traverse((o) => {
    if (!o.isMesh || o.isInstancedMesh || !o.geometry || o.renderOrder !== 0) return;
    for (const m of (Array.isArray(o.material) ? o.material : [o.material])) {
      if (!m || !m.transparent) continue;
      if (!o.geometry.boundingBox) o.geometry.computeBoundingBox();
      const bb = o.geometry.boundingBox.clone().applyMatrix4(o.matrixWorld);
      let l = porMat.get(m);
      if (!l) porMat.set(m, (l = []));
      l.push(bb);
    }
  });
  const ok = new Set();
  for (const [m, caixas] of porMat) {
    let cruza = false;
    for (let i = 0; i < caixas.length && !cruza; i++) {
      for (let j = i + 1; j < caixas.length; j++) {
        if (caixas[i].intersectsBox(caixas[j])) { cruza = true; break; }
      }
    }
    if (!cruza) ok.add(m);
  }
  return ok;
}

export function batchStatic(group) {
  group.updateMatrixWorld(true); // transforms de mundo prontos p/ "assar"
  dedupeMaterials(group); // colapsa materiais idênticos → 1 bucket por APARÊNCIA
  const transpOk = transparentesSeguros(group);
  const buckets = new Map(); // materialUuid|sombras → { mat, cast, recv, geos[] }
  const keep = []; // meshes que NÃO podem ser mesclados (transparência que se cruza/renderOrder)

  group.traverse((o) => {
    if (!o.isMesh || !o.geometry) return;
    const mats = Array.isArray(o.material) ? o.material : [o.material];
    // InstancedMesh JÁ é uma chamada só para N cópias: mesclar destruiria as
    // instâncias (sobraria a geometria-base uma única vez). Passa direto.
    // Transparente que pode se sobrepor a outro do mesmo material também sai:
    // fundir mudaria a ordem de blend (ver transparentesSeguros).
    if (o.isInstancedMesh || o.renderOrder !== 0 ||
        mats.some((m) => m && m.transparent && !transpOk.has(m))) {
      keep.push(o);
      return;
    }
    if (mats.length === 1) {
      add(buckets, mats[0], o, bake(o.geometry.clone(), o));
    } else {
      // multi-material: divide a geometria pelos groups e roteia cada pedaço
      for (const [sub, m] of splitByGroups(o.geometry, mats)) add(buckets, m, o, bake(sub, o));
    }
  });

  const out = new THREE.Group();
  out.name = group.name;
  for (const b of buckets.values()) {
    normalize(b);
    const merged = mergeGeometries(b.geos, false);
    if (!merged) continue; // atributos irreconciliáveis (não deve ocorrer) → pula
    const mesh = new THREE.Mesh(merged, b.mat);
    mesh.castShadow = b.cast;
    mesh.receiveShadow = b.recv;
    mesh.matrixAutoUpdate = false; // estático: matriz nunca muda (identidade)
    out.add(mesh);
  }
  // os não-mescláveis entram como estavam (posição de mundo assada na própria matriz)
  for (const o of keep) {
    const clone = o.clone();
    clone.matrix.copy(o.matrixWorld);
    clone.matrixAutoUpdate = false;
    clone.matrix.decompose(clone.position, clone.quaternion, clone.scale);
    out.add(clone);
  }
  return out;
}

/** Assa a transform de mundo do mesh na geometria (posição/rotação/escala). */
function bake(geo, mesh) {
  geo.applyMatrix4(mesh.matrixWorld);
  return geo;
}

function add(buckets, mat, mesh, geo) {
  const key = mat.uuid + '|' + (mesh.castShadow ? 1 : 0) + (mesh.receiveShadow ? 1 : 0);
  let b = buckets.get(key);
  if (!b) {
    b = { mat, cast: mesh.castShadow, recv: mesh.receiveShadow, geos: [] };
    buckets.set(key, b);
  }
  b.geos.push(geo);
}

/**
 * Divide uma geometria multi-material pelos seus `groups` — devolve pares
 * [subGeometria, material]. Trabalha NÃO-indexado (os groups passam a referenciar
 * vértices diretamente, então o slice dos atributos é um recorte simples).
 */
function splitByGroups(geo, mats) {
  const g = geo.index ? geo.toNonIndexed() : geo;
  const out = [];
  for (const grp of g.groups) {
    const sub = new THREE.BufferGeometry();
    for (const [name, attr] of Object.entries(g.attributes)) {
      const item = attr.itemSize;
      const arr = attr.array.slice(grp.start * item, (grp.start + grp.count) * item);
      sub.setAttribute(name, new THREE.BufferAttribute(arr, item, attr.normalized));
    }
    out.push([sub, mats[grp.materialIndex] ?? mats[0]]);
  }
  return out;
}

/**
 * Deixa as geometrias de um bucket COMPATÍVEIS p/ o mergeGeometries: mesmo
 * indexamento (indexado só se TODAS forem) e mesmos atributos (position+normal;
 * uv só se o material usa textura — sem textura, o uv é peso morto).
 */
function normalize(b) {
  const m = b.mat;
  const useUv = !!(m.map || m.emissiveMap || m.roughnessMap || m.metalnessMap || m.alphaMap || m.aoMap);
  const allIndexed = b.geos.every((g) => !!g.index);
  b.geos = b.geos.map((g) => {
    const x = !allIndexed && g.index ? g.toNonIndexed() : g;
    for (const a of Object.keys(x.attributes)) {
      if (a === 'position' || a === 'normal' || (a === 'uv' && useUv)) continue;
      x.deleteAttribute(a);
    }
    if (!x.attributes.normal) x.computeVertexNormals();
    if (useUv && !x.attributes.uv) {
      x.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(x.attributes.position.count * 2), 2));
    }
    return x;
  });
}
